/**
 * PlayerRosterRow — riga giocatore condivisa tra Gestione Rosa e Rose.
 *
 * Andrea 2026-09-08 (stile Leghe Fantacalcio): altezza fissa, ruoli
 * mantra impilati in verticale a sinistra con badge di dimensione
 * costante, poi avatar, poi nome (+ tag U23) con valore TM e club
 * sotto. A destra uno slot libero per azioni/stato — usato da Gestione
 * Rosa, vuoto in Rose.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { RosaPlayer } from "../../hooks/useFase2";
import { PlayerAvatar } from "./PlayerAvatar";
import { FULL_BY_GROUP } from "./ruoliColors";
import { ruoloMantraClass } from "./ruoliMantra";
import { siglaRuolo } from "./nomenclatura";
import { tr } from "../../i18n";

const RUOLO_CLASSIC_CLASS: Record<string, string> = {
  P: FULL_BY_GROUP.Por,
  D: FULL_BY_GROUP.Def,
  C: FULL_BY_GROUP.Mid,
  A: FULL_BY_GROUP.Att,
};

function classicLetter(r: string | null | undefined): string {
  if (!r) return "?";
  const c = r.charAt(0).toUpperCase();
  return ["P", "D", "C", "A"].includes(c) ? c : "?";
}

/** Etichetta breve del possesso: null se proprietà piena. */
function possessoBadge(
  tipo: RosaPlayer["tipo_possesso"],
): { label: string; tooltip: string } | null {
  switch (tipo) {
    case "prestito_secco":
      return { label: "PREST.", tooltip: tr("Prestito secco") };
    case "prestito_ddr":
      return { label: "DDR", tooltip: tr("Prestito con diritto di riscatto") };
    case "prestito_odr":
      return { label: "ODR", tooltip: tr("Prestito con obbligo di riscatto") };
    case "prestito_odr_condizionato":
      return { label: "ODR-C", tooltip: tr("Prestito con obbligo condizionato") };
    default:
      return null;
  }
}

/** "15M", "2.5M": senza decimali inutili. */
function mln(v: string | number): string {
  const n = Number(v);
  return tr("{{v1}}M", { v1: Number.isInteger(n) ? n : n.toFixed(1) });
}

const ORIGINE_LABEL: Record<string, string> = {
  asta: tr("all'asta"),
  scambio: tr("con uno scambio"),
  acquisto: tr("sul mercato"),
};

interface Props {
  p: RosaPlayer;
  /** Colonna destra (bottoni, stato). Omessa in Rose. */
  right?: ReactNode;
}

export function PlayerRosterRow({ p, right }: Props) {
  const ruoli = p.ruoli_mantra ?? [];
  const classic = classicLetter(p.ruolo_classic);
  const prestito = possessoBadge(p.tipo_possesso);
  const isU23 = p.eta != null && p.eta <= 23;
  // Andrea 2026-09-24: accanto al TM di oggi, di quanto e' cambiato
  // dall'acquisto e quanto e' stato pagato.
  const tmOra = p.valore_tm_milioni != null ? Number(p.valore_tm_milioni) : null;
  const tmIniz = p.valore_tm_iniziale_milioni != null ? Number(p.valore_tm_iniziale_milioni) : null;
  const delta = tmOra !== null && tmIniz !== null ? tmOra - tmIniz : null;
  const origine = p.origine_acquisto
    ? (ORIGINE_LABEL[p.origine_acquisto] ?? p.origine_acquisto)
    : "";

  return (
    <div className="bg-bg-card border border-primary/20 rounded-card px-2 py-1.5 flex items-center gap-2 h-16">
      {/* Ruoli in colonna, larghezza fissa */}
      <div className="w-9 shrink-0 flex flex-col items-center justify-center gap-0.5">
        {ruoli.length > 0 ? (
          ruoli.slice(0, 3).map((rm) => (
            <span
              key={rm}
              className={`w-8 h-4 flex items-center justify-center rounded font-mono font-bold text-[9px] leading-none ${ruoloMantraClass(rm)}`}
            >
              {siglaRuolo(rm)}
            </span>
          ))
        ) : (
          <span
            className={`w-8 h-4 flex items-center justify-center rounded font-mono font-bold text-[9px] leading-none ${
              RUOLO_CLASSIC_CLASS[classic] ?? "bg-fg-muted/20"
            }`}
          >
            {classic}
          </span>
        )}
      </div>

      {/* Andrea 2026-10-15: avatar e nome aprono la scheda giocatore,
          come nel catalogo. */}
      <Link
        to={`/giocatori/${p.giocatore_id}`}
        className="shrink-0 rounded-full ring-accent/60 hover:ring-2"
        title={tr("Apri la scheda giocatore")}
      >
        <PlayerAvatar nome={p.nome} size={36} />
      </Link>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            to={`/giocatori/${p.giocatore_id}`}
            className="font-bold text-sm text-fg truncate hover:text-accent hover:underline"
            title={tr("Apri la scheda giocatore")}
          >
            {p.nome}
          </Link>
          {isU23 && (
            <span
              className="shrink-0 text-[9px] font-bold px-1 rounded border border-success text-success leading-tight"
              title={tr("{{v1}} anni — U23: rinnovo esente", { v1: p.eta })}
            >
              U23
            </span>
          )}
          {prestito && (
            <span
              className="shrink-0 text-[9px] font-bold px-1 rounded bg-warning/20 text-warning border border-warning/40 leading-tight"
              title={prestito.tooltip}
            >
              {prestito.label}
            </span>
          )}
          {p.da_stagione_precedente && (
            <span
              className="shrink-0 text-[9px] font-bold text-accent"
              title={tr("Riconfermato dalla stagione precedente")}
            >
              RIC
            </span>
          )}
        </div>
        {/* Andrea 2026-09-24: colonne a larghezza fissa (TM oggi, variazione,
            pagato, club) cosi' i valori restano incolonnati riga per riga;
            quando un dato manca resta il posto vuoto. */}
        <div className="grid grid-cols-[1.8rem_2.3rem_2.3rem_minmax(0,1fr)] gap-0.5 text-[9.5px] min-[380px]:grid-cols-[1.9rem_2.5rem_2.5rem_minmax(0,1fr)] min-[380px]:text-[10px] sm:grid-cols-[2.4rem_2.9rem_3.2rem_minmax(0,1fr)] sm:gap-1 sm:text-[11px] mt-0.5 min-w-0 items-center">
          <span
            className="font-mono text-accent tabular-nums truncate"
            title={`Valore Transfermarkt di oggi${
              tmIniz !== null ? tr(" · all'acquisto {{v1}}", { v1: mln(tmIniz) }) : ""
            }`}
          >
            {tmOra !== null ? mln(tmOra) : "—"}
          </span>
          <span
            className={`font-mono tabular-nums truncate ${
              delta === null || Math.abs(delta) < 0.05
                ? "text-fg-muted/50"
                : delta > 0
                  ? "text-success"
                  : "text-danger"
            }`}
            title={
              tmIniz !== null
                ? tr("Rispetto al valore TM all'acquisto ({{v1}})", { v1: mln(tmIniz) })
                : tr("Valore TM all'acquisto non disponibile")
            }
          >
            {delta === null
              ? "—"
              : Math.abs(delta) < 0.05
                ? "="
                : tr("{{v1}}{{v2}}", { v1: delta > 0 ? "▲" : "▼", v2: mln(Math.abs(delta)) })}
          </span>
          <span
            className="font-mono text-fg-muted tabular-nums truncate"
            title={
              p.prezzo_acquisto_milioni != null
                ? tr("Pagato {{v1}} {{v2}}", {
                    v1: mln(p.prezzo_acquisto_milioni),
                    v2: origine,
                  }).trim()
                : origine
                  ? tr("Arrivato {{v1}}, senza prezzo", { v1: origine })
                  : tr("Prezzo non disponibile")
            }
          >
            <span className="text-[9px]">💰</span>
            {p.prezzo_acquisto_milioni != null ? mln(p.prezzo_acquisto_milioni) : "—"}
          </span>
          <span className="text-fg-muted truncate">{p.club_serie_a ?? "—"}</span>
        </div>
      </div>

      {/* Andrea 2026-09-08: su mobile le azioni si impilano a destra
          (colonna) invece di occupare larghezza, così i nomi lunghi non
          vengono troncati; da sm in su restano in riga. */}
      {right && (
        <div className="shrink-0 flex flex-col items-center gap-0.5 sm:flex-row sm:items-center sm:gap-1.5">
          {right}
        </div>
      )}
    </div>
  );
}
